import { ElementType, useCallback, useContext, useState } from 'react';
import { Box, List, ListItemButton, ListItemIcon, ListItemText, styled } from '@mui/material';
import { Forum, SmartToy as AgentIcon, School, Handyman, FileCopy, Settings, ExitToApp, PeopleAlt } from '@mui/icons-material';
import { MENU_DRAWER_WIDTH, MENU_MINI_DRAWER_WIDTH } from '@/constants/layout.ts';
import { useNavigate } from 'react-router-dom';
import { AppContext, UserRegistration } from '@my-helpers/common';

const topMenuItems = [
	{ label: 'Conversations', icon: <Forum />, path: '/' },
	{ label: 'Helpers', icon: <AgentIcon />, path: '/helpers' },
	{ label: 'Trainings', icon: <School />, path: '/trainings' },
	{ label: 'Tools', icon: <Handyman />, path: '/tools' },
	{ label: 'Knowledgebase', icon: <FileCopy />, path: '/documents' }
];

const bottomMenuItems = [
	{ label: 'Users', icon: <PeopleAlt />, path: '/users' },
	{ label: 'Admin', icon: <Settings />, path: '/admin' },
];

const AppMenuContainer = styled(Box)(({ theme }) => ({
	borderRight: `1px solid ${theme.palette.divider}`,
	backgroundColor: theme.palette.background.paper,
	overflowY: 'auto',
	overflowX: 'hidden',
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen
	}),
	height: '100%',
	display: 'flex',
	flexDirection: 'column'
}));

interface AppMenuProps {
	sessionUser?: UserRegistration;
}

const AppMenu: ElementType<AppMenuProps> = ({ sessionUser }) => {
	
	const app = useContext(AppContext);
	if (app === undefined) {
		throw new Error('useAppContext must be used within an AppProvider');
	}

	const { clearError } = app.command;

	const navigate = useNavigate();
	const [drawerIsOpen, setIsDrawerOpen] = useState(false);
	const [selectedMenuItem, setSelectedMenuItem] = useState<string | null>(null);

	const handleMenuItemClick = useCallback((path: string, index: number, isBottom: boolean = false) => {
		const currentItemKey = `${isBottom ? 'bottom-' : 'top-'}item-${index}`;

		setSelectedMenuItem(currentItemKey);
		if (!selectedMenuItem || selectedMenuItem === currentItemKey) {
			setIsDrawerOpen(!drawerIsOpen);
		}
		clearError();
		navigate(path);

	}, [drawerIsOpen, selectedMenuItem, navigate, clearError]);

	const renderMenuItems = (items: typeof topMenuItems, isBottom: boolean = false) => {
		return items.map((item, index) => {
			const itemKey = `${isBottom ? 'bottom-' : 'top-'}item-${index}`;
			const isSelected = selectedMenuItem === itemKey;

			return (
				<ListItemButton
					key={itemKey}
					onClick={() => handleMenuItemClick(item.path, index, isBottom)}
					selected={isSelected}
					sx={{
						minHeight: 48,
						justifyContent: drawerIsOpen ? 'initial' : 'center',
						px: 2
					}}
				>
					<ListItemIcon
						sx={{
							minWidth: 0,
							mr: drawerIsOpen ? 1 : 'auto',
							justifyContent: 'center',
						}}
					>
						{item.icon}
					</ListItemIcon>
					<ListItemText
						primary={item.label}
						sx={{
							opacity: drawerIsOpen ? 1 : 0
						}}
					/>
				</ListItemButton>
			);
		});
	};

	return (
		<AppMenuContainer
			width={drawerIsOpen ? MENU_DRAWER_WIDTH : MENU_MINI_DRAWER_WIDTH}
		>
			{/* Top Menu Items */}
			<List sx={{ py: 1, flex: '0 0 auto' }}>
				{renderMenuItems(topMenuItems)}
			</List>
			
			{/* Spacer to push bottom items down */}
			<Box sx={{ flexGrow: 1 }} />
			
			{/* Bottom Menu Items */}
			{sessionUser?.role === 'admin' && (
				<List sx={{ py: 1, flex: '0 0 auto' }}>
					{renderMenuItems(bottomMenuItems, true)}
				</List>
			)}
		</AppMenuContainer>
	);
};

export default AppMenu;
